import { WORKFLOW_NOTES } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, cn, Hint, Icon, Textarea } from "@sagot/ui";
import { useEffect, useId, useRef, useState } from "react";
import { useBuilderTools, type NoteItem } from "./builder-tools";

const N = "engage.workflows.extras.notes";

interface NoteFieldProps {
  item: NoteItem;
  readOnly: boolean;
}

/** A note as a text box: what is typed is saved when the box is left, and Delete removes it. An emptied box goes back to the saved text. */
function NoteField({ item, readOnly }: NoteFieldProps) {
  const { t } = useTranslation();
  const tools = useBuilderTools();
  const [draft, setDraft] = useState(item.note.text);
  const hintId = useId();
  const save = () => {
    const text = draft.trim();
    if (!text) setDraft(item.note.text);
    else if (text !== item.note.text) tools?.editNote(item.id, text);
  };
  return (
    <li className="flex items-start gap-2" data-testid="workflow-note">
      <div className="min-w-0 flex-1">
        <Textarea
          aria-label={t(`${N}.label`)}
          aria-describedby={hintId}
          rows={2}
          maxLength={WORKFLOW_NOTES.textMax}
          readOnly={readOnly}
          className="text-xs"
          value={draft}
          onChange={(event) => { setDraft(event.target.value); }}
          onBlur={save}
        />
        <Hint id={hintId}>{t(`${N}.chars`, { count: draft.length, max: WORKFLOW_NOTES.textMax })}</Hint>
      </div>
      {readOnly ? null : (
        <Button type="button" variant="ghost" size="sm" iconOnly aria-label={t(`${N}.delete`)} onClick={() => { tools?.removeNote(item.id); }}>
          <Icon name="trash" />
        </Button>
      )}
    </li>
  );
}

interface NewNoteProps {
  anchor: string | null;
  /** The box was opened by a button: it takes focus, and Cancel closes it again. */
  onDone?: () => void;
}

/** The box for the next note, with the button that adds it. */
function NewNote({ anchor, onDone }: NewNoteProps) {
  const { t } = useTranslation();
  const tools = useBuilderTools();
  const [text, setText] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (onDone) field.current?.focus();
  }, [onDone]);
  if (!tools) return null;
  const add = () => {
    if (tools.addNote(anchor, text) === null) return;
    setText("");
    onDone?.();
  };
  return (
    <div className="space-y-2">
      <Textarea ref={field} aria-label={t(`${N}.label`)} placeholder={t(`${N}.placeholder`)} rows={2} maxLength={WORKFLOW_NOTES.textMax} className="text-xs" value={text} onChange={(event) => { setText(event.target.value); }} />
      <div className="flex gap-2">
        <Button type="button" variant="secondary" size="sm" disabled={!tools.canAddNote || text.trim() === ""} onClick={add}>
          <Icon name="plus" />
          {t(`${N}.add`)}
        </Button>
        {onDone ? <Button type="button" variant="ghost" size="sm" onClick={onDone}>{t("common.cancel")}</Button> : null}
      </div>
    </div>
  );
}

interface NotesEditorProps {
  /** The path the notes belong to, or `null` for the notes of the workflow. */
  anchor: string | null;
  /** Where a button opens the box for a new note (the strip): `adding` says whether it is open, `onDone` closes it. Left out, the box is always there. */
  adding?: boolean;
  onDone?: () => void;
  className?: string;
}

/** The notes of a path (or of the workflow) to read, edit and delete, and a box to write the next one. */
export function NotesEditor({ anchor, adding, onDone, className }: NotesEditorProps) {
  const tools = useBuilderTools();
  const items = tools?.notesAt(anchor) ?? [];
  const readOnly = !tools?.editable;
  const showNew = !readOnly && (adding ?? true);
  if (items.length === 0 && !showNew) return null;
  return (
    <div className={cn("space-y-3", className)}>
      {items.length > 0 ? (
        <ul className="grid gap-2 sm:grid-cols-2">
          {/* The saved text is in the key: an undo that changes it gives the box its new text. */}
          {items.map((item) => <NoteField key={`${item.id}:${item.note.text}`} item={item} readOnly={readOnly} />)}
        </ul>
      ) : null}
      {showNew ? <NewNote anchor={anchor} onDone={adding === undefined ? undefined : onDone} /> : null}
    </div>
  );
}
